import { useMemo } from 'react'

import { useCanCall, useUsers } from '@ctcells/api'
import { dataSourceFor } from '@ctcells/core'

import type { BatchOwner, BatchOwners } from './batchOwnerColumn.tsx'

import { OwnerNameById } from './OwnerNameById.tsx'

/**
 * 작업 목록 · 이력 · 작업 상세 「작업」 카드의 「작업자」 이름표 — **admin 에게만** 만든다(eric 2026-09-30 「그거 admin 만 작업자 보이게 해줘」).
 *
 * 🔴 **못 부르는 조회는 쏘지 않는다.** `GET /users` · `GET /users/{id}` 는 admin 전용이라(operator · viewer 403 ·
 *    `test/roleAccess.ts`) 판정은 서버가 준 연산 목록(`/auth/me` 의 `listUsers`)으로 한다 — `useCanCall`.
 *    연산 목록을 **모르면(null) 쏘지 않는다**(모니터링의 시료와 반대다 — 여기서 403 은 보일 곳이 없는 요청이고,
 *    열 하나가 한 박자 늦게 서는 편이 낫다). 역할 문자열을 보고 화면이 추론하지 않는다.
 * 🔴 `undefined` 를 돌려주면 화면은 **열 자체를 세우지 않는다** — operator · viewer 는 종전 그대로다.
 * 🔴 이름은 `GET /users` 에서만 온다. `Batch.name` 을 파싱하지 않는다.
 *
 * 표는 두 번 읽는다 — 서버가 `status` 를 안 주면 **비활성 계정을 뺀다**(`users.py` `list_users` · 실측 2026-09-30).
 * 그만둔 사람이 만든 작업도 이름이 서야 해서 `status=disabled` 를 한 번 더 받는다. 그래도 없는 id 는
 * 칸마다 `GET /users/{id}` 로 푼다(`OwnerNameById` · 같은 id 는 쿼리 캐시가 한 번만 부른다).
 * ⚠ 폴링 없음 — 스냅샷 1회다(DP-01). 계정이 바뀌면 관리 화면의 무효화가 같은 키를 다시 부른다.
 */

/** 서버 상한(`users.py` `limit le=500`). 계정 수가 이보다 많아지면 넘친 id 는 개별 조회로 떨어진다 */
const USER_LIMIT = 500

export function useBatchOwners(): BatchOwners | undefined {
  const canList = useCanCall('listUsers')
  const canGet = useCanCall('getUser')
  const on = canList && dataSourceFor('users') === 'live'

  const active = useUsers({ limit: USER_LIMIT, skip: !on })
  const disabled = useUsers({ limit: USER_LIMIT, status: 'disabled', skip: !on })

  return useMemo(() => {
    if (!on) return undefined
    const byId = new Map<string, BatchOwner>()
    for (const user of [...(disabled.data?.items ?? []), ...(active.data?.items ?? [])])
      byId.set(user.user_id, {
        name: user.name === '' ? user.username : user.name,
        username: user.username,
      })
    return {
      byId,
      pending: active.isPending || disabled.isPending,
      /*
       * 🔴 표를 못 받았으면(실패) 개별 조회도 걸지 않는다 — 같은 문이라 행마다 같은 실패가 쏟아진다.
       *    그때 칸은 id 그대로다(지어내지 않는다).
       */
      renderMissing:
        canGet && !active.isError
          ? (userId: string) => <OwnerNameById userId={userId} />
          : undefined,
    }
  }, [on, canGet, active.data, active.isPending, active.isError, disabled.data, disabled.isPending])
}
